import React, { useState, useRef } from 'react';
import { translationService } from '../../services/translationService';
import PageHeader from '../../components/PageHeader';
import PageTransition from '../../components/PageTransition';
import { motion } from 'framer-motion';
import { supportedLanguages, phrases } from '../../config/phrases';

function PhrasePage() {
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [customText, setCustomText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const speechSynthesis = window.speechSynthesis;
  const voices = useRef([]);

  // 初始化语音合成
  React.useEffect(() => {
    const loadVoices = () => {
      voices.current = speechSynthesis.getVoices();
    };

    loadVoices();
    if (speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if (speechSynthesis.speaking) {
        speechSynthesis.cancel();
      }
    };
  }, []);

  // 播放语音
  const speak = (text, lang) => {
    if (speechSynthesis.speaking) {
      speechSynthesis.cancel();
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 0.9;

    const voice = voices.current.find(v => v.lang.startsWith(lang));
    if (voice) {
      utterance.voice = voice;
    }

    speechSynthesis.speak(utterance);
  };

  // 处理自定义文本翻译
  const handleCustomTextSubmit = async (e) => {
    e.preventDefault();
    if (customText.trim()) {
      setIsTranslating(true);
      try {
        const result = await translationService.translateText(customText, {
          targetLanguage: selectedLanguage,
          keepOriginal: true
        });
        setTranslatedText(result.translatedText);
      } catch (error) {
        console.error('翻译错误:', error);
      } finally {
        setIsTranslating(false);
      }
    }
  };

  return (
    <PageTransition className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <PageHeader
        title="常用短语"
        subtitle="点餐必备的常用对话短语"
      />

      {/* 语言选择器 */}
      <div className="sticky top-14 z-40 backdrop-blur-md bg-white/60 border-b border-gray-200/50">
        <div className="container mx-auto px-4 py-1">
          <div className="flex items-center gap-3">
            <span className="text-gray-700 text-sm whitespace-nowrap">目标语言</span>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="flex-1 max-w-[200px] px-4 py-1 rounded-xl border-2 border-gray-200 bg-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-base font-medium appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em',
                paddingRight: '2.5rem'
              }}
            >
              {supportedLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* 自定义翻译输入框 */}
      <div className="container mx-auto px-4 py-4">
        <motion.form
          onSubmit={handleCustomTextSubmit}
          className="backdrop-blur-xl bg-white/60 rounded-2xl shadow-xl p-4 mb-6"
        >
          <div className="flex flex-col space-y-4">
            <div className="relative">
              <input
                type="text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder="输入需要翻译的文字..."
                className="w-full px-4 py-2 pr-10 rounded-xl border border-gray-200 bg-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
              />
              {customText && (
                <button
                  type="button"
                  onClick={() => setCustomText('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100/50 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <div className="flex space-x-2">
              <motion.button
                type="submit"
                disabled={isTranslating}
                className="flex-1 bg-indigo-600 text-white px-6 py-2 rounded-xl hover:bg-indigo-700 transition-colors disabled:bg-indigo-400 shadow-lg shadow-indigo-200/50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isTranslating ? '翻译中...' : '翻译'}
              </motion.button>
              {translatedText && (
                <motion.button
                  type="button"
                  onClick={() => speak(translatedText, selectedLanguage)}
                  className="px-6 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors shadow-lg shadow-green-200/50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  播放翻译
                </motion.button>
              )}
            </div>
          </div>
          {translatedText && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 p-2 bg-white/50 rounded-xl"
            >
              <p className="text-gray-600">{translatedText}</p>
            </motion.div>
          )}
        </motion.form>

        {/* 短语列表 */}
        <div className="space-y-6">
          {phrases.map((category, categoryIndex) => (
            <motion.div
              key={category.category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: categoryIndex * 0.1 }}
              className="backdrop-blur-xl bg-white/60 rounded-2xl shadow-lg overflow-hidden"
            >
              {/* 分类标题 */}
              <div className="bg-indigo-50/50 px-6 py-3 border-b border-indigo-100/50">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {category.category}
                  </h3>
                  <span className="text-sm text-indigo-600">
                    {category.translations[selectedLanguage]}
                  </span>
                </div>
              </div>

              {/* 分类下的短语列表 */}
              <div className="divide-y divide-gray-100">
                {category.items.map((phrase, phraseIndex) => (
                  <motion.div
                    key={phraseIndex}
                    className="px-6 py-4 hover:bg-white/50 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="text-md font-medium text-gray-900">
                          {phrase.text}
                        </div>
                        <div className="text-base text-gray-600 mt-1">
                          {phrase.translations[selectedLanguage]}
                        </div>
                      </div>
                      <motion.button
                        onClick={() => speak(phrase.translations[selectedLanguage], selectedLanguage)}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors"
                        title="播放翻译"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                        </svg>
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </PageTransition>
  );
}

export default PhrasePage; 